import { FC } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button } from '@shared/components/button-new';
import { AtlasLogoWithSuccessIcon } from '@shared/icons/atlas-logo-with-success';
import { ClockIcon } from '@shared/icons/clock';
import { colours } from '@shared/theme/colours';
import { ButtonSize } from '@shared/types/common/button';

import { styles } from './AtlasPublishSuccess.styles';

export interface AtlasPublishSuccessProps extends WithStyles<typeof styles> {
  // New flexible props (preferred)
  bodyText?: string;
  buttonText?: string;
  headerText?: string;
  onBtnClick?: () => void;

  // Legacy props (deprecated but maintained for backward compatibility)
  hiddenFields?: string[];
  rankingErrors?: string[];
  mode?: 'publish' | 'update';
  onReturnToPreviousScreen?: () => void;
}

const AtlasPublishSuccessComponent: FC<AtlasPublishSuccessProps> = ({
  bodyText,
  buttonText,
  classes,
  headerText,
  hiddenFields = [],
  mode = 'publish',
  onBtnClick,
  onReturnToPreviousScreen,
  rankingErrors = [],
}) => {
  const hasRankingErrors = rankingErrors.length > 0;
  const hasHiddenFields = hiddenFields.length > 0;
  const isUpdateMode = mode === 'update';

  const header =
    headerText ||
    (isUpdateMode
      ? t('transaction_successfully_updated', 'Transaction successfully updated')
      : t('transaction_successfully_published', 'Transaction successfully published'));

  const body =
    bodyText ||
    (isUpdateMode
      ? t('this_transaction_has_been_updated', 'This transaction has been updated')
      : hasRankingErrors || hasHiddenFields
      ? t(
          'this_transaction_has_been_published_but_does_not_contribute_towards_your_rankings',
          'This transaction has been published but does not contribute towards your rankings'
        )
      : t(
          'this_transaction_has_been_published_and_now_counts_towards_your_atlas_rankings',
          'This transaction has been published and now counts towards your Atlas rankings'
        ));

  const btnText =
    buttonText || t('return_to_xscreen_namex', 'Return to {{screen_name}}', { screen_name: t('atlas_tab', 'Atlas tab') });

  const handleClick = onBtnClick || onReturnToPreviousScreen;

  return (
    <div className={classes.root}>
      <div className={classes.contentContainer}>
        <div className={classes.iconSection}>
          <AtlasLogoWithSuccessIcon />
        </div>

        <div className={classes.textSection}>
          <h2 className={classes.mainMessage}>{header}</h2>

          <p className={classes.secondaryMessage}>{body}</p>

          <div className={classes.updateNotice}>
            <ClockIcon color={colours.brandLilac[700]} classes={{ root: classes.updateNoticeIcon }} />
            <p className={classes.updateNoticeText}>{t('updates_may_take_up_to_10_minutes_to_reflect_on_atlas')}</p>
          </div>
        </div>
      </div>

      <div className={classes.buttonRow}>
        {handleClick && (
          <Button
            classes={{ root: classes.returnButton }}
            onClick={handleClick}
            size={ButtonSize.medium}
            text={btnText}
            variant="outlined"
          />
        )}
      </div>
    </div>
  );
};

export const AtlasPublishSuccess = withStyles(styles)(AtlasPublishSuccessComponent);
